import React, { useState, useEffect } from 'react';
import apiClient from '../utils/api';

interface Season {
  id: string;
  name: string;
  gameType?: string;
  countryCode?: string;
  isActive?: boolean;
}

interface League {
  id: string;
  name: string;
  description: string;
  creator: {
    id: string;
    teamName: string;
    firstName?: string;
    lastName?: string;
  };
  season: Season;
  type: 'public' | 'private' | 'invite_only' | 'fanleague' | 'official';
  isActive: boolean;
  maxMembers: number;
  currentMembers: number;
  inviteCode?: string;
  groupname?: string;
  createdAt: string;
}

interface EditPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canChangeSeason: boolean;
  canChangeType: boolean;
  canChangeName: boolean;
  canChangeDescription: boolean;
  canChangeMaxMembers: boolean;
  memberCount: number;
  isAdmin: boolean;
  isCreator: boolean;
}

interface EditLeagueModalProps {
  league: League;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedLeague: League) => void;
  onDelete: () => void;
}

const EditLeagueModal: React.FC<EditLeagueModalProps> = ({
  league,
  isOpen,
  onClose,
  onUpdate,
  onDelete
}) => {
  const [formData, setFormData] = useState({
    name: league.name,
    description: league.description,
    season: league.season.id,
    type: league.type,
    maxMembers: league.maxMembers,
    groupname: league.groupname || ''
  });
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [permissions, setPermissions] = useState<EditPermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      setFormData({
        name: league.name,
        description: league.description,
        season: league.season.id,
        type: league.type,
        maxMembers: league.maxMembers,
        groupname: league.groupname || ''
      });
    }
  }, [isOpen, league]);

  const fetchData = async () => {
    try {
      const [seasonsResponse, permissionsResponse] = await Promise.all([
        apiClient.get('/seasons'),
        apiClient.get(`/leagues/${league.id}/can-edit`)
      ]);
      
      setSeasons(seasonsResponse.data);
      setPermissions(permissionsResponse.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Only include groupname if the league type is fanleague
      const submitData: any = { ...formData };
      if (submitData.type !== 'fanleague') {
        delete submitData.groupname;
      }

      const response = await apiClient.put(`/leagues/${league.id}`, submitData);
      onUpdate(response.data.league);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update league');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    try {
      await apiClient.delete(`/leagues/${league.id}`);
      onDelete();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete league');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Edit League
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          {permissions && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                <strong>Current Status:</strong> {permissions.memberCount} members
                {permissions.memberCount > 1 && (
                  <span className="block mt-1 text-orange-600 dark:text-orange-400">
                    Note: Some options are restricted because the league has members.
                  </span>
                )}
              </div>
            </div>
          )}

          {!showDeleteConfirm ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* League Name */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  League Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!permissions?.canChangeName}
                  className="w-full px-3 py-2 border rounded-md input-field"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  disabled={!permissions?.canChangeDescription}
                  className="w-full px-3 py-2 border rounded-md input-field"
                  rows={3}
                />
              </div>

              {/* Season */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Season
                </label>
                <select
                  value={formData.season}
                  onChange={(e) => setFormData({ ...formData, season: e.target.value })}
                  disabled={!permissions?.canChangeSeason}
                  className="w-full px-3 py-2 border rounded-md input-field"
                  required
                >
                  {seasons.map(season => (
                    <option key={season.id} value={season.id}>
                      {season.name}{season.gameType ? ` (${season.gameType})` : ''}
                    </option>
                  ))}
                </select>
                {!permissions?.canChangeSeason && permissions?.memberCount && permissions.memberCount > 1 && (
                  <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                    Cannot change season for leagues with members
                  </p>
                )}
              </div>

              {/* League Type */}
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  League Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as 'public' | 'invite_only' | 'fanleague' | 'official' })}
                  disabled={!permissions?.canChangeType}
                  className="w-full px-3 py-2 border rounded-md input-field"
                  required
                >
                  <option value="public">Public</option>
                  <option value="invite_only">Invite Only</option>
                  {permissions?.isAdmin && (
                    <>
                      <option value="official">Official</option>
                      <option value="fanleague">Fan League</option>
                    </>
                  )}
                </select>
              </div>

              {/* Group Name (only for fanleagues) */}
              {(formData.type === 'fanleague') && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Group Name (URL)
                  </label>
                  <input
                    type="text"
                    value={formData.groupname}
                    onChange={(e) => setFormData({ ...formData, groupname: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    disabled={!permissions?.canChangeType}
                    className="w-full px-3 py-2 border rounded-md input-field"
                    placeholder="e.g. arsenal-fc, man-united"
                    pattern="^[a-z0-9-]+$"
                    minLength={3}
                    maxLength={50}
                    required={formData.type === 'fanleague'}
                  />
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    This creates a custom URL: yoursite.com/{formData.groupname || 'groupname'}
                  </p>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Only lowercase letters, numbers, and hyphens allowed
                  </p>
                </div>
              )}

              {/* Max Members */}
{/*               <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Maximum Members
                </label>
                <input
                  type="number"
                  min={permissions?.memberCount || 2}
                  max={10000}
                  value={formData.maxMembers}
                  onChange={(e) => setFormData({ ...formData, maxMembers: parseInt(e.target.value) })}
                  disabled={!permissions?.canChangeMaxMembers}
                  className="w-full px-3 py-2 border rounded-md input-field"
                  required
                />
                {permissions?.memberCount && formData.maxMembers < permissions.memberCount && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                    Cannot be less than current member count ({permissions.memberCount})
                  </p>
                )}
              </div>
 */}
              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <div>
                  {permissions?.canDelete && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                    >
                      Delete League
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary px-4 py-2 text-sm rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !permissions?.canEdit}
                    className="btn-primary px-4 py-2 text-sm rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update League'}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            /* Delete Confirmation */
            <div className="text-center">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Delete League
              </h3>
              <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to delete "{league.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary px-4 py-2 text-sm rounded-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={loading}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Deleting...' : 'Delete League'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditLeagueModal; 